import { Link } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import BrandWordmark from "@/components/BrandWordmark";
import OrgCoBrand from "@/components/OrgCoBrand";
import IfModuleVisible from "@/components/IfModuleVisible";
import { useAuth } from "@/contexts/AuthContext";
import { hasOperatorAccess, isFreeBuyerUser } from "@/lib/access";
import { orgScopedPath } from "@/lib/orgDoor";

/** Plan level a link needs — mirrors the sidebar gating and the /choose-plan
 *  compare table. "member" = any paid plan (Explorer or Operator). */
type Access = "public" | "member" | "operator";

type FooterLink = {
  label: string;
  to?: string;
  href?: string;
  access?: Access;
  /** Module-visibility key — hidden when the viewer's org switched it off. */
  module?: string;
  /** `to` is relative to the viewer's role area (/buyer, /seller, /bl-advisor). */
  roleScoped?: boolean;
};

const LINK_COLUMNS: { heading: string; links: FooterLink[] }[] = [
  {
    heading: "Marketplace",
    links: [
      { label: "Available Listings", to: "/" },
      // The modules roadmap, not deal browsing — the pipeline deals live in the
      // home marketplace's Coming Soon tab. Resolved against the viewer's role
      // area: the bare path redirects to the BUYER page, which bounces sellers
      // and advisors to "/".
      { label: "Upcoming Modules", to: "/upcoming-modules", access: "member", module: "upcoming_modules", roleScoped: true },
      { label: "AI Deal Matching", to: "/buyer/deal-scoring", access: "member" },
      { label: "Favorites", to: "/favorites", access: "member" },
    ],
  },
  {
    heading: "Tools",
    links: [
      { label: "SBA Calculator", to: "/buyer/sba-calculator", access: "member" },
      { label: "Valuation Calculator", to: "/buyer/valuation-calculator", access: "member" },
      { label: "Deal CRM", to: "/buyer/pipeline", access: "operator" },
      { label: "My Interests", to: "/my-interests", access: "member" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "About", href: "https://www.businesslocating.com/about" },
      { label: "Support", to: "/support" },
      { label: "Contact", href: "mailto:support@businesslocatinghub.com" },
    ],
  },
];

const linkClass = "text-sm text-foreground/80 transition-colors hover:text-primary";

export default function Footer({ logoVariant = "auto", logoLockup = false }: { logoVariant?: "auto" | "dark" | "light"; /** Use the live-text lockup instead of the baked-in wordmark image (landing page). */ logoLockup?: boolean }) {
  const { user } = useAuth();

  // Hide links the signed-in user's plan can't reach, so the footer never routes
  // a Basic buyer into an upgrade wall (and never shows Explorer the Operator-only
  // Deal CRM). Signed-out visitors keep every link — those are marketing entry
  // points and the routes send them to sign-in.
  const freeBuyer = isFreeBuyerUser(user);
  const operatorAccess = hasOperatorAccess(user);
  const canSee = (link: FooterLink): boolean => {
    if (link.access === "member") return !freeBuyer;
    if (link.access === "operator") return !user || operatorAccess;
    return true;
  };

  // Same role order as Navigation's roleBase. Admins have no roadmap page of
  // their own and are allowed on the buyer one. Signed out keeps the bare path,
  // which App.tsx redirects (and RoleRoute sends to sign-in).
  const roles = user?.roles ?? [];
  const roleBase = !user
    ? ""
    : roles.some((r) => r === "admin" || r === "org_admin")
      ? "/buyer"
      : roles.includes("bl_advisor")
        ? "/bl-advisor"
        : roles.includes("seller")
          ? "/seller"
          : "/buyer";
  const resolveTo = (link: FooterLink): string | undefined =>
    link.to && link.roleScoped && roleBase ? orgScopedPath(`${roleBase}${link.to}`, user?.org_slug) : link.to;

  const columns = LINK_COLUMNS
    .map((col) => ({ ...col, links: col.links.filter(canSee) }))
    .filter((col) => col.links.length > 0);

  // Track the surviving column count so a fully-gated column (Tools, for a Basic
  // buyer) doesn't leave a hole in the grid. Literal classes — Tailwind can't
  // generate an arbitrary value built at runtime.
  const gridClass =
    columns.length >= 3
      ? "lg:grid-cols-[1.5fr_1fr_1fr_1fr]"
      : columns.length === 2
      ? "lg:grid-cols-[1.5fr_1fr_1fr]"
      : "lg:grid-cols-[1.5fr_1fr]";

  return (
    <footer className="mt-auto border-t border-border/80 bg-card/80 px-4 py-10 sm:px-6 lg:px-8">
      <div className="w-full">
        <div className={`grid grid-cols-1 gap-10 sm:grid-cols-2 ${gridClass}`}>
          {/* Brand */}
          <div className="max-w-xs">
            <div className="flex items-center gap-3">
              {logoLockup ? (
                <BrandWordmark pinSize={44} />
              ) : (
                <BrandLogo variant={logoVariant} className="h-12 w-auto" />
              )}
              {/* Organization users see their own logo beside the platform mark. */}
              <OrgCoBrand className="h-14 max-w-[9rem]" dividerClassName="h-9" />
            </div>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              The marketplace that finds the perfect business fit &mdash; vetted
              listings, AI deal scoring and financing tools in one place.
            </p>
          </div>

          {/* Link columns */}
          {columns.map((col) => (
            <div key={col.heading}>
              <div className="text-[0.65rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                {col.heading}
              </div>
              <ul className="mt-4 space-y-2.5">
                {col.links.map((link) => (
                  <IfModuleVisible key={link.label} module={link.module}>
                  <li>
                    {link.to ? (
                      <Link to={resolveTo(link)!} className={linkClass}>
                        {link.label}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        target={link.href?.startsWith("http") ? "_blank" : undefined}
                        rel={link.href?.startsWith("http") ? "noopener noreferrer" : undefined}
                        className={linkClass}
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                  </IfModuleVisible>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-border/60 pt-5 text-xs text-muted-foreground">
          <span>&copy; {new Date().getFullYear()} BusinessLocating Hub &middot; All rights reserved</span>

          <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex items-center gap-1.5">
              <a
                href="https://www.facebook.com/BusinessLocating"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="flex h-7 w-7 items-center justify-center rounded-lg border border-border/60 bg-background text-muted-foreground transition-colors hover:border-[#1877F2]/40 hover:text-[#1877F2]"
              >
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor">
                  <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/>
                </svg>
              </a>
              <a
                href="https://www.instagram.com/thebusinesslocating"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="flex h-7 w-7 items-center justify-center rounded-lg border border-border/60 bg-background text-muted-foreground transition-colors hover:border-[#E1306C]/40 hover:text-[#E1306C]"
              >
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </a>
              {/* Canonical company URL. The share links LinkedIn hands out are
                  /authwall?...&sessionRedirect=<this>, which bounces a
                  signed-out visitor into a sign-in wall instead of the page. */}
              <a
                href="https://www.linkedin.com/company/business-locating"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="flex h-7 w-7 items-center justify-center rounded-lg border border-border/60 bg-background text-muted-foreground transition-colors hover:border-[#0A66C2]/40 hover:text-[#0A66C2]"
              >
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z"/>
                </svg>
              </a>
              <a
                href="https://x.com/bizlocating"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X"
                className="flex h-7 w-7 items-center justify-center rounded-lg border border-border/60 bg-background text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground"
              >
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
            </div>

            <a
              href="https://www.businesslocating.com/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:text-foreground"
            >
              Terms &amp; Conditions
            </a>
            <a
              href="https://www.businesslocating.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:text-foreground"
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
